/** Страница редактора: верхняя панель, редактор, живой предпросмотр, проблемы. */
import { classicToV2, isDraftEmpty, v2ToClassic } from '../../../src/shared/transform.js';
import { validateDraft, type Issue } from '../../../src/shared/validate.js';
import { newContainer, newDraft, newText } from '../../../src/shared/factory.js';
import type { DAction, Draft } from '../../../src/shared/types.js';
import { DEMO, api, type Template } from '../api.js';
import { app, channelById } from '../ctx.js';
import { clear, h, icon, iconButton, plural } from '../dom.js';
import { renderMessage } from '../preview/render.js';
import { themeButton } from '../theme.js';
import { store } from '../store.js';
import { confirmDialog, modal, toast } from '../ui/overlay.js';
import { navigate } from '../router.js';
import { classicEditor } from './classic.js';
import { segmented } from './common.js';
import { openJsonModal } from './json.js';
import { attachmentsSection, optionsSection, profileSection } from './sections.js';
import { openSendModal } from './send.js';
import { starterClassic, starterV2 } from './starter.js';
import { loadTemplate, openTemplatesModal, saveCurrentTemplate } from './templates.js';
import { v2Editor } from './v2.js';

let cleanup: (() => void) | null = null;

export async function editorPage(root: HTMLElement, params: URLSearchParams) {
  cleanup?.();
  const g = app.guild!;
  const restored = store.attach(g.id);

  // параметры из ссылки бота
  const draftId = params.get('draft');
  const tplId = params.get('tpl');
  if (draftId) {
    try {
      const res = await api.draft(g.id, draftId);
      store.replace(res.data, { editTarget: params.get('edit') === '1' ? res.editTarget : null, templateId: null, templateName: null });
      res.warnings.slice(0, 3).forEach((w) => toast(w, 'info'));
      if (params.get('edit') === '1' && res.editTarget) toast('Режим редактирования: «Сохранить» обновит исходное сообщение', 'ok');
    } catch (err: any) {
      toast(err.message, 'error');
    }
    history.replaceState(null, '', `#/g/${g.id}/editor`);
  } else if (tplId) {
    await loadTemplate(tplId, false);
    history.replaceState(null, '', `#/g/${g.id}/editor`);
  } else if (!restored) {
    const s = starterClassic();
    const firstText = g.channels.find((c) => c.type === 0);
    if (firstText) s.embeds[0].description = s.embeds[0].description.replace('000000000000000000', firstText.id);
    store.replace(s, { editTarget: null, templateId: null, templateName: null });
  }

  /* ---------- каркас ---------- */
  const editorCol = h('div', { class: 'editor-col', attrs: { 'aria-label': 'Редактор' } });
  const surface = h('div', { class: ['dc-surface', `theme-${app.previewTheme}`, app.previewMobile && 'is-mobile'] });
  const ephemeralBox = h('div', { class: 'ephemeral-box' });
  const issuesList = h('div', { class: 'issues' });
  const issuesBadge = h('span', { class: 'issues-badge' });
  const banners = h('div', { class: 'banners' });
  const undoBtn = iconButton('undo', 'Отменить (Ctrl+Z)', () => store.undo());
  const redoBtn = iconButton('redo', 'Повторить (Ctrl+Shift+Z)', () => store.redo());

  const modeSeg = h('div', { class: 'mode-slot' });
  const renderModeSeg = () => {
    modeSeg.replaceChildren(
      segmented(
        [
          { value: 'classic', label: 'Классика', icon: 'layers' },
          { value: 'v2', label: 'Components V2', icon: 'box' },
        ],
        store.draft.mode,
        (v) => switchMode(v as Draft['mode']),
        'Формат сообщения',
      ),
    );
  };

  const switchMode = (mode: Draft['mode']) => {
    const d = store.draft;
    if (d.mode === mode) return;
    if (store.editTarget?.wasV2 && mode === 'classic') {
      toast('Сообщение уже в V2 - Discord не позволяет вернуть его в классический вид', 'error');
      renderModeSeg();
      return;
    }
    if (isDraftEmpty(d)) {
      store.replace(mode === 'v2' ? { ...starterV2(), profile: d.profile, options: d.options, attachments: d.attachments } : { ...newDraft('classic'), profile: d.profile, options: d.options, attachments: d.attachments });
      return;
    }
    const m = modal({ title: mode === 'v2' ? 'Перейти на Components V2?' : 'Вернуться к классике?', icon: 'wand' });
    m.body.append(
      h(
        'p',
        { class: 'muted' },
        mode === 'v2'
          ? 'Текст станет текстовым блоком, каждый эмбед - контейнером с тем же цветом, кнопки сохранятся. Опрос в V2 не поддерживается.'
          : 'Контейнеры станут эмбедами, текст вне контейнеров - текстом сообщения. Часть оформления (секции, разделители) упростится.',
      ),
    );
    m.footer.append(
      h('button', { class: 'btn', type: 'button', onclick: () => { m.close(); renderModeSeg(); } }, 'Отмена'),
      h(
        'button',
        {
          class: 'btn',
          type: 'button',
          onclick: () => {
            m.close();
            store.replace({ ...newDraft(mode), profile: d.profile, options: d.options, attachments: d.attachments, ...(mode === 'v2' ? { v2: [newContainer()] } : {}) });
          },
        },
        'Начать с пустого',
      ),
      h(
        'button',
        {
          class: 'btn btn-primary',
          type: 'button',
          onclick: () => {
            m.close();
            store.replace(mode === 'v2' ? classicToV2(d) : v2ToClassic(d));
            toast('Содержимое преобразовано', 'ok');
          },
        },
        icon('wand', 16),
        'Преобразовать',
      ),
    );
  };

  const topbar = h(
    'header',
    { class: 'topbar glass' },
    h(
      'button',
      { class: 'guild-chip', type: 'button', title: 'Сменить сервер', onclick: () => navigate('#/') },
      g.iconUrl ? h('img', { src: g.iconUrl, alt: '' }) : h('span', { class: 'guild-fallback' }, g.name.slice(0, 1)),
      h('span', { class: 'guild-name' }, g.name),
      icon('chevron', 14),
    ),
    modeSeg,
    h('div', { class: 'grow' }),
    h('div', { class: 'history-group', attrs: { role: 'group', 'aria-label': 'История изменений' } }, undoBtn, redoBtn),
    h(
      'div',
      { class: 'tb-group' },
      h('button', { class: 'btn btn-ghost', type: 'button', title: 'JSON, импорт, загрузка по ссылке', onclick: () => openJsonModal() }, icon('code', 16), h('span', { class: 'hide-sm' }, 'JSON')),
      h('button', { class: 'btn btn-ghost', type: 'button', title: 'Шаблоны сервера', onclick: () => openTemplatesModal() }, icon('folder', 16), h('span', { class: 'hide-sm' }, 'Шаблоны')),
      themeButton(),
      g.canManage ? iconButton('shield', 'Доступ и настройки сервера', () => navigate(`#/g/${g.id}/settings`)) : null,
      iconButton('trash', 'Новый пустой черновик', async () => {
        if (await confirmDialog('Очистить черновик?', 'Всё содержимое будет удалено (можно вернуть через Ctrl+Z).', 'Очистить', true)) {
          const mode = store.draft.mode;
          store.replace({ ...newDraft(mode), ...(mode === 'v2' ? { v2: [newText('')] } : {}) }, { editTarget: null, templateId: null, templateName: null });
        }
      }),
    ),
    h('button', { class: 'btn btn-primary send-btn', type: 'button', onclick: () => openSendModal() }, icon('send', 16), h('span', null, 'Отправить')),
  );

  const previewHead = h(
    'div',
    { class: 'preview-head' },
    h('div', { class: 'preview-title' }, icon('eye', 16), 'Предпросмотр'),
    h('div', { class: 'grow' }),
    iconButton(app.previewTheme === 'dark' ? 'sun' : 'moon', 'Светлая / тёмная тема Discord', (e) => {
      app.previewTheme = app.previewTheme === 'dark' ? 'light' : 'dark';
      previewCol.querySelectorAll('.dc-surface').forEach((s) => {
        s.classList.toggle('theme-dark', app.previewTheme === 'dark');
        s.classList.toggle('theme-light', app.previewTheme === 'light');
      });
      (e.currentTarget as HTMLElement).replaceChildren(icon(app.previewTheme === 'dark' ? 'sun' : 'moon', 16));
    }),
    iconButton(app.previewMobile ? 'monitor' : 'phone', 'Ширина: компьютер / телефон', (e) => {
      app.previewMobile = !app.previewMobile;
      previewCol.querySelectorAll('.dc-surface').forEach((s) => s.classList.toggle('is-mobile', app.previewMobile));
      (e.currentTarget as HTMLElement).replaceChildren(icon(app.previewMobile ? 'monitor' : 'phone', 16));
    }),
    issuesBadge,
  );

  const previewCol = h(
    'aside',
    { class: 'preview-col', attrs: { 'aria-label': 'Предпросмотр' } },
    h('div', { class: 'preview-card glass' }, previewHead, h('div', { class: 'surface-scroll' }, surface, ephemeralBox), issuesList),
  );

  const workspace = h('main', { class: 'workspace' }, editorCol, previewCol);
  const mobileTabs = h(
    'nav',
    { class: 'mobile-tabs glass' },
    h('button', { class: 'active', type: 'button', onclick: (e: MouseEvent) => setMobileTab('editor', e) }, icon('edit', 16), 'Редактор'),
    h('button', { type: 'button', onclick: (e: MouseEvent) => setMobileTab('preview', e) }, icon('eye', 16), 'Предпросмотр'),
  );
  const setMobileTab = (t: 'editor' | 'preview', e: MouseEvent) => {
    workspace.dataset.tab = t;
    mobileTabs.querySelectorAll('button').forEach((b) => b.classList.toggle('active', b === e.currentTarget));
  };
  workspace.dataset.tab = 'editor';

  clear(root);
  root.append(h('div', { class: 'editor-page' }, topbar, banners, workspace, mobileTabs));

  /* ---------- рендер ---------- */

  const renderBanners = () => {
    const items: HTMLElement[] = [];
    const et = store.editTarget;
    if (et) {
      const ch = channelById(et.channelId);
      items.push(
        h(
          'div',
          { class: 'banner banner-edit glass' },
          icon('edit', 16),
          h('span', null, 'Редактирование сообщения в ', h('strong', null, ch ? `#${ch.name}` : 'канале'), et.via === 'webhook' ? ' (вебхук)' : ''),
          h('a', { class: 'link', href: `https://discord.com/channels/${g.id}/${et.channelId}/${et.messageId}`, target: '_blank', rel: 'noreferrer' }, 'открыть'),
          h('div', { class: 'grow' }),
          h('button', { class: 'btn btn-sm', type: 'button', onclick: () => openSendModal() }, icon('save', 14), 'Сохранить в сообщение'),
          h('button', { class: 'btn btn-sm btn-ghost', type: 'button', title: 'Отвязать: следующая отправка создаст новое сообщение', onclick: () => store.setMeta({ editTarget: null }) }, icon('x', 14)),
        ),
      );
    }
    if (store.templateId) {
      items.push(
        h(
          'div',
          { class: 'banner glass' },
          icon('folder', 16),
          h('span', null, 'Шаблон ', h('strong', null, `«${store.templateName}»`)),
          h('div', { class: 'grow' }),
          h('button', { class: 'btn btn-sm', type: 'button', onclick: () => saveCurrentTemplate() }, icon('save', 14), 'Сохранить шаблон'),
          h('button', { class: 'btn btn-sm btn-ghost', type: 'button', title: 'Отвязать от шаблона', onclick: () => store.setMeta({ templateId: null, templateName: null }) }, icon('x', 14)),
        ),
      );
    }
    if (DEMO) {
      items.push(h('div', { class: 'banner banner-demo glass' }, icon('info', 16), h('span', null, 'Демо-режим: данные сервера ненастоящие, отправка имитируется. Шаблоны хранятся в браузере.')));
    }
    banners.replaceChildren(...items);
  };

  const renderEditor = () => {
    const scroll = editorCol.scrollTop;
    const d = store.draft;
    editorCol.replaceChildren(...(d.mode === 'classic' ? classicEditor() : v2Editor()), attachmentsSection(), profileSection(), optionsSection(), h('div', { class: 'editor-footer muted small' }, 'Ctrl+Z / Ctrl+Shift+Z - отмена и повтор · Ctrl+S - сохранить шаблон · Ctrl+Enter - отправить'));
    editorCol.scrollTop = scroll;
    renderModeSeg();
  };

  let previewQueued = false;
  const renderPreview = () => {
    if (previewQueued) return;
    previewQueued = true;
    requestAnimationFrame(() => {
      previewQueued = false;
      const d = store.draft;
      const useWebhook = !!d.profile.username || !!d.profile.avatarUrl;
      surface.replaceChildren(
        renderMessage(d, {
          author: { name: useWebhook && d.profile.username ? d.profile.username : g.bot.username, avatarUrl: useWebhook && d.profile.avatarUrl ? d.profile.avatarUrl : g.bot.avatarUrl },
          onAction: simulateAction,
          edited: !!store.editTarget,
        }),
      );
    });
  };

  const renderIssues = () => {
    const issues: Issue[] = validateDraft(store.draft, { edit: store.editTarget ? { wasV2: store.editTarget.wasV2 } : undefined });
    const errs = issues.filter((i) => i.level === 'error').length;
    const warns = issues.length - errs;
    issuesBadge.className = ['issues-badge', errs ? 'is-error' : warns ? 'is-warn' : 'is-ok'].join(' ');
    issuesBadge.replaceChildren(icon(errs ? 'alert' : warns ? 'info' : 'check', 14), errs ? `${errs} ${plural(errs, 'ошибка', 'ошибки', 'ошибок')}` : warns ? `${warns} ${plural(warns, 'замечание', 'замечания', 'замечаний')}` : 'Готово к отправке');
    issuesList.replaceChildren(
      ...issues.slice(0, 30).map((i) =>
        h(
          'button',
          {
            class: ['issue', `issue-${i.level}`],
            type: 'button',
            onclick: () => focusItem(i.itemId),
          },
          icon(i.level === 'error' ? 'alert' : 'info', 14),
          h('span', { class: 'issue-where' }, i.where),
          h('span', { class: 'issue-msg' }, i.message),
        ),
      ),
    );
  };

  const focusItem = (id?: string) => {
    if (!id) return;
    const el = editorCol.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`);
    if (!el) return;
    // раскрываем родителей
    let p: HTMLElement | null = el;
    while (p) {
      if (p.classList.contains('is-collapsed')) (p.querySelector(':scope > header') as HTMLElement | null)?.click();
      p = p.parentElement?.closest('.item-card, .panel') ?? null;
    }
    workspace.dataset.tab = 'editor';
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('flash');
    setTimeout(() => el.classList.remove('flash'), 1200);
  };

  /* ---------- имитация нажатий в предпросмотре ---------- */
  const tplCache = new Map<string, Template>();
  const simulateAction = async (a: DAction, label: string) => {
    let ephemeral: Draft | null = null;
    if (a.type === 'template') {
      if (!a.templateId) return toast('У кнопки не выбран шаблон', 'error');
      try {
        const t = tplCache.get(a.templateId) ?? (await api.template(g.id, a.templateId));
        tplCache.set(a.templateId, t);
        ephemeral = { ...t.data, poll: null };
      } catch (err: any) {
        return toast(err.message, 'error');
      }
    } else if (a.type === 'role') {
      const r = g.roles.find((x) => x.id === a.roleId);
      if (!r) return toast('У кнопки не выбрана роль', 'error');
      ephemeral = newDraft('v2');
      const c = newContainer();
      c.accent = 0x3ba55d;
      c.children = [newText(`### Роли обновлены\n${a.mode === 'remove' ? 'Снято' : 'Выдано'}: <@&${r.id}>`)];
      ephemeral.v2 = [c];
    } else if (a.type === 'raw') {
      return toast(`custom_id «${a.customId}» обработает другой бот`, 'info');
    } else {
      return toast(`«${label || 'Кнопка'}» ничего не делает - выберите действие`, 'info');
    }
    ephemeralBox.replaceChildren(
      h('div', { class: 'ephemeral-label' }, icon('mouse', 13), `Так увидит ответ тот, кто нажал «${label || 'кнопку'}»`, iconButton('x', 'Скрыть', () => ephemeralBox.replaceChildren())),
      h('div', { class: ['dc-surface', `theme-${app.previewTheme}`, app.previewMobile && 'is-mobile'] }, renderMessage(ephemeral, { author: { name: g.bot.username, avatarUrl: g.bot.avatarUrl }, ephemeral: true, onAction: simulateAction })),
    );
    ephemeralBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const updateUndo = () => {
    undoBtn.disabled = !store.canUndo();
    redoBtn.disabled = !store.canRedo();
  };

  const unsub = store.subscribe((kind) => {
    if (kind === 'structure') {
      renderEditor();
      renderBanners();
    }
    renderPreview();
    renderIssues();
    updateUndo();
  });

  const onKey = (e: KeyboardEvent) => {
    if (document.querySelector('.modal-overlay')) return;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    const inText = (e.target as HTMLElement)?.matches?.('input, textarea');
    if (k === 'z' && !e.shiftKey && !inText) {
      e.preventDefault();
      store.undo();
    } else if (((k === 'z' && e.shiftKey) || k === 'y') && !inText) {
      e.preventDefault();
      store.redo();
    } else if (k === 's') {
      e.preventDefault();
      saveCurrentTemplate();
    } else if (k === 'enter') {
      e.preventDefault();
      openSendModal();
    }
  };
  document.addEventListener('keydown', onKey);

  cleanup = () => {
    unsub();
    document.removeEventListener('keydown', onKey);
  };

  renderEditor();
  renderBanners();
  renderPreview();
  renderIssues();
  updateUndo();

  if (params.get('templates') === '1') openTemplatesModal();
}
